import { Box, Button, Center, Flex, HStack } from "@chakra-ui/react";
import CDS from "design-system";
import { useCallback, useMemo, useState } from "react";
import { NewHireJourneyId } from "models/newHire";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { SortOption } from "components/ControlBar/types";
import { useNewHireJourneyMeetingsQuery } from "features/Resources/Meeting/hooks";
import { filterMeetingsByText, sortMeetings } from "features/Resources/Meeting/helpers";
import { BaseMeetingSchedulingTable } from "features/Resources/Meeting/Components/Table/MeetingSchedulingTable";
import ActionSideDrawer from "components/ActionSideDrawer";
import useActionSideDrawer from "components/ActionSideDrawer/hooks";
import { BulkScheduleView } from "features/BulkSchedule";
import { useCurrentUserQuery } from "features/User/hooks";

interface MeetingsByListProps {
  newHireJourneyId: NewHireJourneyId;
}

const SORT_OPTIONS: SortOption[] = [
  { label: "Date: Soonest First", value: "date_asc" },
  { label: "Date: Latest First", value: "date_desc" },
];

const DEFAULT_SORT = "date_asc";

export default function MeetingsByList({ newHireJourneyId }: MeetingsByListProps) {
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [showBulkSchedule, setShowBulkSchedule] = useState(false);
  const { isOpen, onClose, selectedActionPrefixId, expandClickHandlerByPrefixId } = useActionSideDrawer();
  const { data: currentUser } = useCurrentUserQuery();
  const timezone = currentUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: meetings, isLoading } = useNewHireJourneyMeetingsQuery(newHireJourneyId);

  const handleReset = useCallback(() => {
    setSearchText("");
    setSort(DEFAULT_SORT);
  }, []);

  const filteredMeetings = useMemo(
    () => sortMeetings(filterMeetingsByText(meetings, searchText), sort === "date_desc" ? "desc" : "asc"),
    [meetings, searchText, sort],
  );

  const hasPendingMeetings = useMemo(() => meetings?.some((m) => m.status === "pending_scheduling"), [meetings]);

  if (isLoading) {
    return (
      <Center mt="4">
        <CDS.Text14 color="fg.emphasized">Loading...</CDS.Text14>
      </Center>
    );
  }

  return (
    <Flex direction="column" width="100%">
      <HStack mt="2" justify="space-between" align="center">
        <Box flex={1}>
          <FilterSortSearchBar
            searchPlaceholder="Search meetings..."
            searchValue={searchText}
            onSearchChange={setSearchText}
            currentSort={sort}
            defaultSort={DEFAULT_SORT}
            onSortChange={(val) => setSort(val || DEFAULT_SORT)}
            sortOptions={SORT_OPTIONS}
            activeFilters={[]}
            onReset={handleReset}
            hasActiveFilters={!!searchText || sort !== DEFAULT_SORT}
          />
        </Box>
        {hasPendingMeetings && (
          <Button colorScheme="blue" size="sm" flexShrink={0} onClick={() => setShowBulkSchedule(true)}>
            Schedule All
          </Button>
        )}
      </HStack>
      <BaseMeetingSchedulingTable data={filteredMeetings} onExpand={expandClickHandlerByPrefixId} />
      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedActionPrefixId} />
      <BulkScheduleView
        journeyId={String(newHireJourneyId)}
        timezone={timezone}
        isOpen={showBulkSchedule}
        onClose={() => setShowBulkSchedule(false)}
      />
    </Flex>
  );
}
